/**
 * @file User-generated content formatting, sanitization, and mention helpers.
 */

import { appData } from '../../app/state/state.js';
import DOMPurify from 'https://cdn.jsdelivr.net/npm/dompurify@3.0.6/+esm';
import { getUserDisplayName } from './userDisplay.js';

const BULLET_LIST_CLASSES = 'my-1 list-disc pl-5';

/**
 * Converts dash-prefixed lines into simple unordered lists and preserves
 * leading-space indentation as padded block lines.
 * @param {string} html Inline-formatted content.
 * @returns {string} Content with list and indentation block markup.
 */
function formatListAndIndentedLines(html) {
    const outputLines = [];
    let listItems = [];

    const flushList = () => {
        if (listItems.length === 0) {return;}
        outputLines.push(
            `<ul class="${BULLET_LIST_CLASSES}">${listItems.map((item) =>
                `<li>${item}</li>`).join('')}</ul>`,
        );
        listItems = [];
    };

    html.split('\n').forEach((line) => {
        const listMatch = line.match(/^-\s+(.*)$/);
        if (listMatch) {
            listItems.push(listMatch[1]);
            return;
        }

        flushList();

        const indentationMatch = line.match(/^\s{2,}(.*)$/);
        if (indentationMatch) {
            outputLines.push(`<div class="pl-4">${indentationMatch[1]}</div>`);
            return;
        }

        outputLines.push(line);
    });

    flushList();
    return outputLines.join('\n');
}

/**
 * Converts URLs within a string into clickable HTML links and replaces newlines with <br>.
 * @param {string | null} text - The input text.
 * @returns {string} The text with URLs converted to links and newlines to <br>.
 */
export const linkifyText = (text) => {
    if (!text) {return '';}
    // Regex to find URLs (starting with http, https, or www)
    const urlRegex = /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])|(\bwww\.[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/ig;

    // Replace URLs with anchor tags
    const linkedText = text.replace(urlRegex, (url) => {
        let href = url;
        if (!href.match(/^[a-zA-Z]+:\/\//)) { // Add http:// if missing (for www links)
            href = 'http://' + href;
        }
        return `<a href="${href}" target="_blank" class="text-blue-600 hover:underline">${url}</a>`;
    });

    // Replace newlines with <br> tags
    return linkedText.replace(/\n/g, '<br>');
};

/**
 * Escapes HTML entities in a string to prevent XSS.
 * Use this for plain text that should never contain HTML.
 * @param {string} str - The string to escape.
 * @returns {string} The escaped string safe for HTML insertion.
 */
export function escapeHTML(str) {
    if (!str || typeof str !== 'string') {return '';}
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
}

/**
 * Sanitizes HTML content to prevent XSS attacks while preserving safe formatting.
 * Uses DOMPurify to remove dangerous HTML/JavaScript while allowing safe tags (links, spans, etc.).
 * Should be used on all user-generated content before rendering with innerHTML.
 * @param {string} html - The HTML string to sanitize.
 * @returns {string} The sanitized HTML string safe for innerHTML usage.
 */
export function sanitizeHTML(html) {
    if (!html || typeof html !== 'string') {return '';}
    // DOMPurify removes script tags, event handlers, and other dangerous content
    // while preserving safe HTML like links, spans, and formatting
    return DOMPurify.sanitize(html, {
        ALLOWED_TAGS: ['a', 'span', 'br', 'strong', 'em', 'u', 'p', 'div', 'b', 'i', 'ul', 'li', 'blockquote'],
        ALLOWED_ATTR: ['href', 'target', 'class', 'style'],
        ALLOW_DATA_ATTR: false
    });
}

/**
 * Sanitizes Markdown-rendered HTML for attachment previews.
 * Keeps common document structure while stripping unsafe attributes and tags.
 * @param {string} html - Markdown-rendered HTML.
 * @returns {string} Sanitized HTML safe for innerHTML usage.
 */
export function sanitizeMarkdownPreviewHTML(html) {
    if (!html || typeof html !== 'string') {return '';}
    return DOMPurify.sanitize(html, {
        ALLOWED_TAGS: [
            'a', 'blockquote', 'br', 'code', 'del', 'div', 'em', 'h1', 'h2',
            'h3', 'h4', 'h5', 'h6', 'hr', 'li', 'ol', 'p', 'pre', 's', 'span',
            'strong', 'table', 'tbody', 'td', 'th', 'thead', 'tr', 'ul',
        ],
        ALLOWED_ATTR: ['colspan', 'href', 'rel', 'rowspan', 'target'],
        ALLOW_DATA_ATTR: false
    });
}

/**
 * Formats and sanitizes user-generated content for safe display.
 * Combines formatContentForDisplay (handles @mentions), linkifyText (handles URLs),
 * and sanitizeHTML (XSS protection) into a single safe operation.
 * Use this for all user-generated content before rendering with innerHTML.
 * @param {string | null} text - The raw user-generated text from the database.
 * @returns {string} The formatted and sanitized HTML safe for innerHTML usage.
 */
export function formatAndSanitizeUserContent(text) {
    if (!text || typeof text !== 'string') {return '';}
    // Step 1: Format @mentions and convert to HTML
    let processed = formatContentForDisplay(text);

    // Step 2: Convert URLs to links (Inline logic from linkifyText but WITHOUT newline conversion)
    const urlRegex = /(\b(https?|ftp|file):\/\/[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])|(\bwww\.[-A-Z0-9+&@#\/%?=~_|!:,.;]*[-A-Z0-9+&@#\/%=~_|])/ig;
    processed = processed.replace(urlRegex, (url) => {
        let href = url;
        if (!href.match(/^[a-zA-Z]+:\/\//)) {
            href = 'http://' + href;
        }
        return `<a href="${href}" target="_blank" class="text-blue-600 hover:underline">${url}</a>`;
    });

    // Step 3: Markdown Parsing (Bold, Italic, Lists, Indentation)
    // NOTE: Perform this BEFORE replacing newlines with <br> so '^' matches effectively.

    // Bold: **text** -> <strong>text</strong>
    processed = processed.replace(/(\*\*)(.*?)\1/g, '<strong>$2</strong>');

    // Italic: *text* -> <em>text</em> (underscore excluded to prevent false matches on words_like_this)
    processed = processed.replace(/(\*)(.*?)\1/g, '<em>$2</em>');

    // Blockquote: > text -> <blockquote>text</blockquote>
    processed = processed.replace(/^>\s?(.*)$/gm, '<blockquote class="border-l-4 border-gray-300 pl-4 py-1 my-2 italic">$1</blockquote>');

    // Lists: Lines starting with "- " render as bullet lists. Lines starting
    // with 2+ spaces remain plain indentation.
    processed = formatListAndIndentedLines(processed);

    // Step 4: Clean up newlines after block elements to prevent double spacing
    processed = processed.replace(/(<\/(div|blockquote|ul)>)\n/g, '$1');

    // Step 5: Convert remaining newlines to <br> tags
    processed = processed.replace(/\n/g, '<br>');

    // Step 6: Sanitize to prevent XSS
    return sanitizeHTML(processed);
}

/**
 * Swaps internal IDs for readable names (e.g., "@[123]" -> "@Alex S")
 * and wraps them in a styled span for visual emphasis.
 * Used when rendering text from the database.
 * @param {string} text - The raw text from DB.
 * @returns {string} The HTML string with styled tags.
 */
export function formatContentForDisplay(text) {
    if (!text || typeof text !== 'string') {return text;}
    const users = appData?.users || [];

    // Regex matches the database format @[USER_ID]
    return text.replace(/@\[([^\]]+)\]/g, (match, uid) => {
        const user = users.find(u => u.id === uid);
        if (user) {
            const displayName = getUserDisplayName(user, users);
            // Return blue text for the name
            return `<span class="text-blue-600 dark:text-blue-400 font-medium">@${escapeHTML(displayName)}</span>`;
        }
        // If user not found, still show as a tag but with the UID
        // This prevents raw @[UID] from appearing and ensures span is always created
        return `<span class="text-blue-600 dark:text-blue-400 font-medium">@${escapeHTML(uid)}</span>`;
    });
}

/**
 * Converts @[ID] format to @Name format for textarea editing.
 * Used when loading database content into textarea so users see friendly names.
 * @param {string} text - The text from database (may contain @[ID] format).
 * @returns {string} Plain text with @Name format for mentions.
 */
export function convertIdsToNamesForEditing(text) {
    if (!text || typeof text !== 'string') {return text;}
    const users = appData?.users || [];

    // Convert @[ID] to @Name format
    return text.replace(/@\[([^\]]+)\]/g, (match, uid) => {
        const user = users.find(u => u.id === uid);
        if (user) {
            const displayName = getUserDisplayName(user, users);
            return `@${displayName}`;
        }
        // If user not found, keep the @[ID] format
        return match;
    });
}

/**
 * Extracts plain text from HTML, converting formatted @mentions back to @[ID] format.
 * Used when loading content into textarea for editing (handles corrupted HTML that was accidentally saved).
 * @param {string} html - The HTML string (may contain formatted spans).
 * @returns {string} Plain text with @[ID] format for mentions.
 */
export function extractPlainTextFromHTML(html) {
    if (!html || typeof html !== 'string') {return html;}
    const users = appData?.users || [];

    // First check if this is already plain text (not HTML) - if so, return as-is
    // This handles the case where rawValue is already @[ID] format from database
    if (!html.includes('<') && !html.includes('>')) {
        // It's plain text, return as-is (it's already in @[ID] format)
        return html;
    }

    // Create a temporary DOM element to parse HTML
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;

    // Extract text content, preserving @mentions
    let plainText = '';
    const walker = document.createTreeWalker(
        tempDiv,
        NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
        null
    );

    let node;
    while ((node = walker.nextNode())) {
        if (node.nodeType === Node.TEXT_NODE) {
            plainText += node.textContent;
        } else if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'SPAN') {
            // Check if this is a @mention span
            const spanText = node.textContent || '';
            if (spanText.startsWith('@')) {
                // Try to find the user by display name
                const displayName = spanText.substring(1);
                const user = users.find(u => {
                    const userName = getUserDisplayName(u, users);
                    return userName === displayName;
                });
                if (user) {
                    plainText += `@[${user.id}]`;
                } else {
                    plainText += spanText;
                }
            } else {
                plainText += spanText;
            }
        }
    }

    return plainText || tempDiv.textContent || html;
}

/**
 * Swaps readable names back to internal IDs (e.g., "@Alex S" -> "@[123]").
 * Used before saving text to the database.
 * @param {string} text - The human-readable text.
 * @returns {string} The text ready for DB storage.
 */
export function formatContentForSave(text) {
    if (!text || typeof text !== 'string') {return text;}
    const users = appData?.users || [];

    // Build a map of "Display Name" -> "ID"
    // We sort by length (descending) to ensure we match "Alex Smith" before "Alex"
    const nameMap = users.map(u => ({
        name: getUserDisplayName(u, users),
        id: u.id
    })).sort((a, b) => b.name.length - a.name.length);

    let processedText = text;
    nameMap.forEach(u => {
        // Regex matches @Name ensuring it ends with a word boundary
        // We escape special regex characters in the name just in case
        const safeName = u.name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`@${safeName}\\b`, 'g');
        processedText = processedText.replace(regex, `@[${u.id}]`);
    });

    return processedText;
}
